import { defineEffect } from '../../../core/effect';
import { pulse, seg, tween } from '../../../core/effect/motion';
import { fade, place } from '../fx/common';
import { COST_SLASH, lifeNumberGlyphWidth, lifeNumberTexture, makeLifeNumber, tagPlace } from '../fx/lifeNumber';

/**
 * **LP を払うコスト**（Issue #579。指示書 `../direction/pay_lpcost.md`）。
 *
 * だれがいくら払ったかを、払う人の LP のすぐ下に数字で見せる。**数字の描き方は damage と同じ**（`../fx/lifeNumber.js`。
 * 銀の金属文字＋黒の縁・先頭の「-」・後ろの斬り傷の差し）で、違うのは**差しの色（オレンジ）・大きさ（damage より小さい）・
 * 出方（その場に出る。飛ばさない・叩きつけない）だけ**（ユーザー 2026-09-28）。「COST」の文字は出さない。
 *
 * ```
 * 0     LP の数字のすぐ下に「-4000」（オレンジの斬り傷の差しが外側から伸び、数字が少し上から降りて止まる）
 * 150   数字を見せたまま、LP の数字とゲージが減る前の値から本来の値へ減る（`hud` の `life`）
 * 500   LP の枠が小さく揺れる（damage より控えめ。閃光・光は無い）
 * 650   数字が薄くなって消える
 * 800   終わり（何も残らない）
 * ```
 *
 * 演出は payload（`player` 払う人・`amount` 払う値）とその瞬間の HUD の値だけ。もやもやした光は使わない。
 */
const D = 800;
const OPEN = 150;
const DROP_FROM = 150;
const DROP_TO = 500;
const SHAKE_AT = 500;
const FADE_FROM = 650;

/** 数字の大きさ（px）。damage（170）より小さく */
export const SIZE = 104;
/** 差し色（damage の赤 #ff2e3c と分ける。数字の後ろの斬り傷は `COST_SLASH`） */
export const COST_COLOR = '#ff8a1e';

/** その間 HUD に見せる LP（減る前 → 150〜500ms で本来の値へ）。値が無ければ null（頼まない） */
export function shownLife(hud, player, t) {
  const p = player === 1 ? 1 : 0;
  const from = hud?.lifeFrom?.[p];
  const to = hud?.lifeTarget?.[p];
  if (!Number.isFinite(from) || !Number.isFinite(to) || !(from > to)) return null;
  if (t < DROP_FROM) return from;
  return from + (to - from) * tween(t, DROP_FROM, DROP_TO, 'ease_out');
}

/** 数字の置き場所は recover と共有（`../fx/lifeNumber.js::tagPlace`） */
export { tagPlace };

export default defineEffect({
  situation: 'pay_lpcost',
  role: 'effect',
  label: 'LP のコスト',
  color: COST_COLOR,
  opens({ payload }) {
    if (!Number.isFinite(payload?.amount) || payload.amount <= 0) return null;
    return payload;
  },
  duration: () => D,
  sounds: () => [{ at: DROP_FROM, name: 'life_down', gain: 0.6 }],
  hud: ({ payload, localMs, hud }) => {
    const life = shownLife(hud, payload.player, localMs);
    return [{
      anchor: 'life',
      player: payload.player,
      ...(life != null ? { life } : {}),
      shake: 0.45 * pulse(localMs, { at: SHAKE_AT, inMs: 10, hold: 40, outMs: 100 }),
      color: COST_COLOR,
      phase: localMs,
    }];
  },

  layers: [{
    id: 'number',
    label: 'コストの数字',
    space: 'screen',
    script: (ctx) => {
      const { THREE, kit } = ctx;
      const { payload } = ctx;
      const root = new THREE.Group();
      // 数字の描き方は damage と同じ（`../fx/lifeNumber.js`）。違うのは差しの色と大きさだけ
      const tex = lifeNumberTexture(kit, payload.amount, SIZE);
      const glyphW = Math.max(SIZE, lifeNumberGlyphWidth(tex, SIZE));
      const glyphH = Math.round(SIZE * 1.25);
      const { x: cx, y: cy, out } = tagPlace(ctx, payload.player, glyphW, glyphH);
      const n = makeLifeNumber(ctx, root, tex, { order: 10, colors: COST_SLASH, name: 'lpcost-number' });

      return {
        root,
        update: ({ localMs: t }) => {
          // その場に出る: 差しが外側から伸び、数字が少し上から降りて止まる（飛ばない・叩きつけない）
          const open = tween(t, 0, 120, 'expo_out');
          n.lay(open);
          const drop = tween(t, 0, OPEN, 'back_out');
          const alpha = t < D ? Math.min(1, seg(t, 0, 90)) * (1 - seg(t, FADE_FROM, D)) : 0;
          // 差しの伸びる向き: 外側の端から内側へ（差しの中心を外側へずらしておく）
          n.slashes.forEach((sl) => { sl.m.position.x = sl.skew + out * (sl.w * (1 - open)) / 2; });
          place(n.g, cx, cy - 16 * (1 - drop), {});
          fade(n.g, alpha);
        },
      };
    },
  }],
}, 'pay_lpcost_effect.js');
